import { Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Slider, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { findRearCamera, useQrScanner } from '../../component/qrScan/useQrScanner';
import { Card } from '../../styled/components';

const READER_ID = 'qr-test-reader';

const RESOLUTIONS = [
  { label: '480p', width: 640, height: 480 },
  { label: '720p', width: 1280, height: 720 },
  { label: '1080p', width: 1920, height: 1080 },
  { label: 'Max', width: 3840, height: 2160 },
];

type DecodeRecord = {
  text: string;
  at: string;
};

export const QrCodeTest = (): ReactElement => {
  const decodeTimestampsRef = useRef<number[]>([]);
  const lastDecodeStartRef = useRef<number>(0);

  const [cameras, setCameras] = useState<{ id: string; label: string }[] | null | undefined>(undefined);
  const [cameraId, setCameraId] = useState<string>('');
  const [fps, setFps] = useState(8);
  const [qrboxPct, setQrboxPct] = useState(55);
  const [resolutionIdx, setResolutionIdx] = useState(RESOLUTIONS.length - 1);
  const [continuousFocus, setContinuousFocus] = useState(true);
  const [useBarcodeDetector, setUseBarcodeDetector] = useState(true);
  const [focusDistance, setFocusDistance] = useState<number | ''>('');
  const [history, setHistory] = useState<DecodeRecord[]>([]);
  const [uniqueCodes, setUniqueCodes] = useState<string[]>([]);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [camerasError, setCamerasError] = useState<string | null>(null);
  const [showCaps, setShowCaps] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const {
    start,
    running,
    error: scanError,
    capabilities,
    zoom,
    setZoom,
    torch,
    setTorch,
    readerBoxRef,
  } = useQrScanner({
    readerId: READER_ID,
    onDecode: (decodedText) => {
      const now = performance.now();
      setLatencyMs(Math.round(now - lastDecodeStartRef.current));
      lastDecodeStartRef.current = now;
      const tsNow = Date.now();
      decodeTimestampsRef.current.push(tsNow);
      decodeTimestampsRef.current = decodeTimestampsRef.current.filter((t) => tsNow - t < 10000);
      setHistory((h) => [{ text: decodedText, at: new Date().toLocaleTimeString() }, ...h].slice(0, 10));
      setUniqueCodes((prev) => (prev.includes(decodedText) ? prev : [...prev, decodedText]));
    },
    tuning: {
      fps,
      qrboxPct,
      resolution: RESOLUTIONS[resolutionIdx],
      continuousFocus,
      useBarcodeDetector,
      focusDistance: focusDistance === '' ? null : focusDistance,
    },
  });

  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devs) => {
        setCameras(devs);
        if (devs.length === 0) {
          setCamerasError('No cameras found.');
          return;
        }
        setCameraId(findRearCamera(devs)?.id ?? '');
      })
      .catch((e) => {
        setCameras(null);
        setCamerasError(e?.message ?? 'Camera permission denied or no camera available.');
      });
  }, []);

  // (Re)start the scanner whenever the camera or a restart-requiring tuning value changes —
  // `start`'s identity changes with the tuning it closes over.
  useEffect(() => {
    if (!cameraId) return;
    lastDecodeStartRef.current = performance.now();
    start(cameraId).catch(() => {
      /* surfaced via the hook's error state */
    });
  }, [cameraId, start]);

  const error = camerasError ?? scanError;

  const decodeRate = useMemo(() => {
    const now = Date.now();
    const recent = decodeTimestampsRef.current.filter((t) => now - t < 10000);
    return recent.length / 10;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <Helmet title="QR Code Test" />
      <Card sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          QR Code Test
        </Typography>

        {cameras === undefined && <Typography>Loading cameras…</Typography>}

        {cameras === null && (
          <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography variant="h6">Camera not available</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Click the camera icon in the address bar to grant permission, then reload.
            </Typography>
          </Box>
        )}

        {Array.isArray(cameras) && cameras.length > 0 && (
          <>
            <Tabs value={cameraId} onChange={(_, v) => setCameraId(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
              {cameras.map((c) => (
                <Tab key={c.id} value={c.id} label={c.label || `Camera ${c.id.slice(0, 6)}`} />
              ))}
            </Tabs>

            <Box
              id={READER_ID}
              ref={readerBoxRef}
              sx={{
                width: '100%',
                maxWidth: 600,
                mx: 'auto',
                minHeight: 200,
                bgcolor: 'grey.200',
                mb: 2,
                touchAction: 'pan-y',
              }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Last decoded:</strong> {history[0]?.text ?? '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Decode latency:</strong> {latencyMs !== null ? `${latencyMs} ms` : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Decode rate (last 10 s):</strong> {decodeRate.toFixed(1)}/s
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {running ? 'running' : 'starting…'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recent decodes
              </Typography>
              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None yet
                </Typography>
              ) : (
                <Box component="ol" sx={{ pl: 4, m: 0 }}>
                  {history.map((h, i) => (
                    <li key={`${h.at}-${i}`}>
                      <code>{h.text}</code> <span style={{ color: '#888' }}>({h.at})</span>
                    </li>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Unique codes scanned: {uniqueCodes.length}
              </Typography>
              {uniqueCodes.length > 0 && (
                <Box component="ol" sx={{ pl: 4, m: 0 }}>
                  {uniqueCodes.map((code, i) => (
                    <li key={`${i}-${code}`}>
                      <code>{code}</code>
                    </li>
                  ))}
                </Box>
              )}
            </Box>

            {error && (
              <Box sx={{ p: 2, bgcolor: 'warning.light', mt: 2, borderRadius: 1 }}>
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <Box sx={{ mt: 4, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <Button variant="text" size="small" onClick={() => setShowSettings((s) => !s)}>
                {showSettings ? 'Hide' : 'Show'} settings & diagnostics
              </Button>
              {showSettings && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 2 }}>
                    <Box>
                      <Typography gutterBottom>FPS: {fps}</Typography>
                      <Slider value={fps} onChange={(_, v) => setFps(v as number)} min={5} max={30} step={1} valueLabelDisplay="auto" />
                    </Box>

                    <Box>
                      <Typography gutterBottom>qrbox size: {qrboxPct}% of video</Typography>
                      <Slider value={qrboxPct} onChange={(_, v) => setQrboxPct(v as number)} min={30} max={100} step={5} valueLabelDisplay="auto" />
                    </Box>

                    <FormControl size="small">
                      <InputLabel>Resolution</InputLabel>
                      <Select value={resolutionIdx} label="Resolution" onChange={(e) => setResolutionIdx(Number(e.target.value))}>
                        {RESOLUTIONS.map((r, i) => (
                          <MenuItem key={r.label} value={i}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={<Switch checked={continuousFocus} onChange={(e) => setContinuousFocus(e.target.checked)} />}
                      label="Continuous focus"
                    />

                    <FormControlLabel
                      control={<Switch checked={useBarcodeDetector} onChange={(e) => setUseBarcodeDetector(e.target.checked)} />}
                      label="Native BarcodeDetector (when available)"
                    />

                    {capabilities?.torch !== undefined && (
                      <FormControlLabel control={<Switch checked={torch} onChange={(e) => setTorch(e.target.checked)} />} label="Torch" />
                    )}

                    {zoom !== null && capabilities?.zoom && (
                      <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                        <Typography gutterBottom>
                          Zoom: {zoom.toFixed(2)}× (range {capabilities.zoom.min}–{capabilities.zoom.max})
                        </Typography>
                        <Slider
                          value={zoom}
                          onChange={(_, v) => setZoom(v as number)}
                          min={capabilities.zoom.min}
                          max={capabilities.zoom.max}
                          step={capabilities.zoom.step || 0.1}
                          valueLabelDisplay="auto"
                        />
                      </Box>
                    )}

                    {capabilities?.focusDistance && (
                      <TextField
                        size="small"
                        label={`focusDistance (${capabilities.focusDistance.min}–${capabilities.focusDistance.max})`}
                        type="number"
                        value={focusDistance}
                        onChange={(e) => setFocusDistance(e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    )}
                  </Box>

                  <Box>
                    <Button variant="text" size="small" onClick={() => setShowCaps((s) => !s)}>
                      {showCaps ? 'Hide' : 'Show'} track capabilities
                    </Button>
                    {showCaps && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.100',
                          mt: 1,
                          fontSize: 12,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          overflow: 'auto',
                          maxHeight: 240,
                        }}
                      >
                        {capabilities ? JSON.stringify(capabilities, null, 2) : 'no capabilities yet'}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Card>
    </>
  );
};

export default QrCodeTest;
